import { useGetDesignersByAccountIdQuery } from "features/api/designerApi";
import { useGetUserDetailsQuery } from "features/api/userApi";
import { useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { models } from "types/api/viewModels";
import useInitOrderDetail from "./useInitOrderDetail";
import { useParams } from "react-router";
import { sortDesigners } from "components/NewOrders/utils/NewOrderUtils";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { DesignerIsNotHereText } from "components/NewOrders/constants";
import { useUserLocation } from "hooks/useUserLocation.ts";

const useNewOrderDesigners = () => {
	const { isAddOnPage, isNewQuotePage, isReplacementsPage } = useUserLocation();

	const draftOrder = useSelector((state: RootState) => state.newOrder.draftDetails?.draftOrder);
	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const defaultConfiguration = newOrderDetails?.configurations?.[0] ?? draftOrder?.configurations?.[0];
	const accountNumberId = defaultConfiguration?.accountId ?? null;

	const params = useParams();
	const formContext = useFormContext();
	const setValue = useMemo(() => formContext?.setValue ?? (() => {}), [formContext]);
	const dispatch = useDispatch();

	const { orderDetail: originalOrderDetails } = useInitOrderDetail(params.orderId);
	const { data: user } = useGetUserDetailsQuery();
	const { data: designers, isLoading: isLoadingDesigners } = useGetDesignersByAccountIdQuery(accountNumberId ?? "", {
		skip: isReplacementsPage || !accountNumberId || isNewQuotePage
	});

	const [isCustomDesigner, setIsCustomDesigner] = useState(false);

	const designerOptions = useMemo(() => {
		let newDesignerList: models["DesignerViewModel"][] = [...(designers || [])];

		return sortDesigners(
			newDesignerList
				.filter(
					(designer): designer is models["DesignerViewModel"] =>
						typeof designer !== "string" && designer.designerNumber != null
				)
				.filter(
					(item, index, arr) =>
						arr.findIndex((el) => typeof el !== "string" && el.designerNumber === item.designerNumber) ===
						index
				)
		);
	}, [designers]);

	const findDesignerNumberInDesignerResponse = useCallback(
		(designerNumber: string | null | undefined): models["DesignerViewModel"] | undefined | null =>
			designerOptions?.find((designer) => designer.designerNumber === designerNumber),
		[designerOptions]
	);

	const convertDesignerNumberToDraftDesigner = (
		designerNumber: string | null | undefined
	): { designerName?: string | null; designerNumber?: string | null } => {
		const foundDesignerNumber = findDesignerNumberInDesignerResponse(designerNumber);
		if (foundDesignerNumber) {
			return {
				designerName: [foundDesignerNumber.firstName, foundDesignerNumber.lastName].filter(Boolean).join(" "),
				designerNumber: foundDesignerNumber.designerNumber
			};
		} else if (originalOrderDetails.designer?.designerNumber === designerNumber) {
			return {
				designerName: [originalOrderDetails.designer?.firstName, originalOrderDetails.designer?.lastName]
					.filter(Boolean)
					.join(" "),
				designerNumber: originalOrderDetails.designer?.designerNumber
			};
		} else {
			return {
				designerName: null,
				designerNumber: designerNumber ?? null
			};
		}
	};

	const addDesignerNumberToOrder = (designerNumber: string | null | undefined) => {
		dispatch(newOrderActions.overwriteOrderDetails({ ...newOrderDetails, designerNumber: designerNumber }));
		dispatch(
			newOrderActions.overwriteDraftOrder({
				...draftOrder,
				designerName: convertDesignerNumberToDraftDesigner(designerNumber).designerName,
				designerNumber: convertDesignerNumberToDraftDesigner(designerNumber).designerNumber
			})
		);
	};

	const clearDesigner = () => {
		setValue("designer", null);
		setValue("customDesigner", undefined);
		setIsCustomDesigner(false);
		addDesignerNumberToOrder(null);
	};

	const setDefaultDesigner = useCallback(() => {
		if (isReplacementsPage) {
			return;
		}

		const designerNumber = newOrderDetails?.designerNumber;

		if (designerNumber === null) {
			return;
		}

		const originalOrderDesignerNumber = originalOrderDetails?.designer?.designerNumber;
		const foundDesignerInDesignerResponse = findDesignerNumberInDesignerResponse(
			designerNumber ?? originalOrderDetails?.designer?.designerNumber
		);

		const setCustomDesigner = (number: string) => {
			setValue("designer", DesignerIsNotHereText);
			setValue("customDesigner", number);
			setIsCustomDesigner(true);
		};

		const setDesignerDropdown = (designer: any) => {
			setValue("designer", designer);
			setIsCustomDesigner(false);
		};

		// ADD ON LOGIC
		if (isAddOnPage) {
			// first: check if designer exists in /designers response (either from redux or original order)
			// if exists, set as dropdown option
			if (foundDesignerInDesignerResponse) {
				setDesignerDropdown(foundDesignerInDesignerResponse);
				return;
			}

			// second: if not found in response, but we have a designer number in redux, set as custom designer
			if (designerNumber) {
				setCustomDesigner(designerNumber);
				return;
			}

			// fallback to null
			setValue("designer", null);
			return;
		}

		// NEW ORDER LOGIC
		// first: if designer number (either redux or original number) exists but not found in /designers response, set as custom designer
		if (!foundDesignerInDesignerResponse && (designerNumber || originalOrderDesignerNumber)) {
			setCustomDesigner(designerNumber ?? originalOrderDesignerNumber ?? "");
			return;
		}

		// second: if designer IS found in /designers response, set as designer dropdown option
		if (foundDesignerInDesignerResponse) {
			setDesignerDropdown(foundDesignerInDesignerResponse);
			return;
		}

		// third: if designers response only has one option, set that as default
		if (designerOptions?.length === 1) {
			setValue("designer", designerOptions[0]);
			return;
		}

		// fourth: if designer number exists in /user response, set as designer dropdown option
		const foundDesignerInUserResponse = designerOptions?.find(
			(designer) => user?.designerNumber && designer?.designerNumber?.includes(user?.designerNumber)
		);
		if (foundDesignerInUserResponse) {
			setValue("designer", foundDesignerInUserResponse);
		}
	}, [
		isReplacementsPage,
		newOrderDetails?.designerNumber,
		originalOrderDetails?.designer,
		findDesignerNumberInDesignerResponse,
		designerOptions,
		isAddOnPage,
		user?.designerNumber,
		setValue
	]);

	return {
		findDesignerNumberInDesignerResponse,
		setDefaultDesigner,
		clearDesigner,
		addDesignerNumberToOrder,
		designerOptions,
		isLoadingDesigners,
		isCustomDesigner,
		setIsCustomDesigner
	};
};

export default useNewOrderDesigners;
